import { bool2str, KeyCode, MediaQuery, componentId } from '../../util';

class PrimaryNavigation {

    constructor(element) {
        this._addEventListeners = this._addEventListeners.bind(this);
        this._removeEventListeners = this._removeEventListeners.bind(this);
        this._openItem = this._openItem.bind(this);
        this._onItemClick = this._onItemClick.bind(this);
        this._onResize = this._onResize.bind(this);
        this._onKeyPress = this._onKeyPress.bind(this);
        this._onBlur = this._onBlur.bind(this);
        this._resetAriaAttributes = this._resetAriaAttributes.bind(this);
        this._removeAriaAttributes = this._removeAriaAttributes.bind(this);
        this._adjustPopupProperties = this._adjustPopupProperties.bind(this);
        this.openItem = this.openItem.bind(this);
        /**
         * @type {HTMLDivElement}
         */
        this._element = element;
        /**
         * @type {HTMLAnchorElement[]}
         * @private
         */
        this._megamenuItems = Array.from(this._element.querySelectorAll('.js-megamenu-item-on-desktop'));
        /**
         * @type {HTMLDivElement[]}
         */
        this._megamenuPanels = this._megamenuItems.map(e => e.nextSibling).filter(e => e !== null);
        /**
         *  @type {ToggleButton|null}
         */
        this._toggleButton = window['nav-primary__mobile-toggle'];
        this._setupToggleButton();
        this._isDesktop = null;
        this._megamenuItems.forEach(e => {
            const elId = `megamenu-panel-${componentId()}`;
            if (e.nextSibling) {
                e.setAttribute('data-controls', elId);
                e.nextSibling.setAttribute('id', elId);
                e.setAttribute('aria-controls', elId);
                e.nextSibling.setAttribute('id', elId);
            }
        });
        this._resetAriaAttributes();

        this._addEventListeners();
        this._onResize();
    }

    _setupToggleButton() {
        this._toggleButton.button.addEventListener('open', () => this._enableBackdrop(true));
        this._toggleButton.button.addEventListener('close', () => this._enableBackdrop(false));
    }

    openItem(element, targetState) {
        if (this._element.contains(element)) {
            this._openItem(element, targetState);
        } else {
            throw new ReferenceError('Given element is not part of this PrimaryNavigation');
        }
        this._adjustPopupProperties();
    }

    get isOpen() {
        return this._element.getAttribute('aria-expanded') === 'true';
    }

    get isMobile() {
        return document.querySelector('.js-nav-primary__mobile-toggle').offsetParent !== null;
    }

    get height() {
        return this._element.offsetTop + this._element.clientHeight;
    }

    _resetAriaAttributes() {
        this._megamenuItems.forEach(e => {
            if (e.hasAttribute('data-controls')) {
                e.setAttribute('role', 'button');
                e.setAttribute('aria-expanded', 'false');
            }
            const panel = e.nextSibling;
            if (panel) {
                panel.setAttribute('aria-hidden', 'true');
            }
        });
    }

    _removeAriaAttributes() {
        this._megamenuItems.forEach(e => {
            e.removeAttribute('role');
            e.removeAttribute('aria-expanded');
            const panel = e.nextSibling;
            if (panel) {
                panel.removeAttribute('aria-hidden');
            }
        });
    }

    _openItem(el, targetState) {
        if (el === null) {
            return;
        }
        this._resetAriaAttributes();
        if ((el.getAttribute('aria-expanded') === 'true') !== targetState) {
            el.setAttribute('aria-expanded', bool2str(targetState));
            const panel = el.nextSibling;
            if (panel) {
                panel.setAttribute('aria-expanded', bool2str(targetState));
                panel.setAttribute('aria-hidden', bool2str(!targetState));
            }
        }
        this._adjustPopupProperties();
    }

    _setPanelPositions() {
        const wrapperWidth = this._element.closest('nav').clientWidth;
        const offset = (document.documentElement.clientWidth - wrapperWidth) / 2;
        this._megamenuPanels.forEach(e => {
            e.style.left = `${-offset}px`;
            e.style.right = `${-offset}px`;
            e.style.paddingLeft = `${offset}px`;
            e.style.paddingRight = `${offset}px`;
        });
    }

    _resetPanelPositions() {
        this._megamenuPanels.forEach(e => {
            e.style.left = '0px';
            e.style.right = '0px';
            e.style.paddingLeft = '0px';
            e.style.paddingRight = '0px';
        });
    }

    /**
     * @param {boolean} value
     * @private
     */
    _enableBackdrop(value) {
        const op = value ? 'add' : 'remove';
        document.querySelector('.js-nav-primary__backdrop')
            .classList[op]('nav-primary__backdrop--active');
        document.querySelector('.services-panel__wrapper')
            .classList[op]('services-panel__wrapper--background');
        this._adjustPopupProperties();
    }

    _adjustPopupProperties() {
        if (!this.isMobile || !this._toggleButton.open) {
            document.body.style.minHeight = '';
            document.body.style.height = '';
            document.body.style.overflow = '';
        } else {
            document.body.style.minHeight = `${this.height}px`;
            document.body.style.height = '100%';
            document.body.style.overflow = 'hidden';
        }
    }

    /**
     * @param {MouseEvent} e
     * @private
     */
    _onItemClick(e) {
        e.preventDefault();
        const el = e.currentTarget;
        this._openItem(el, el.getAttribute('aria-expanded') !== 'true');
    }

    _onResize() {
        const shouldBeDesktop = window.matchMedia('only screen and ' + MediaQuery.navViewport).matches;
        if (shouldBeDesktop !== this._isDesktop) {
            this._isDesktop = shouldBeDesktop;
            this._toggleButton.open = shouldBeDesktop;
            this._enableBackdrop(false);
        } else if (shouldBeDesktop) {
            this._setPanelPositions();
        }
        this._adjustPopupProperties();
    }

    /**
     * @param {KeyboardEvent} e
     * @private
     */
    _onKeyPress(e) {
        if (e.keyCode === KeyCode.ESCAPE && !this._isDesktop) {
            this._toggleButton.open = false;
        }
        if (this.isOpen && this.isMobile && e.keyCode === KeyCode.TAB && !e.ctrlKey) {
            const links = Array.from(this._element.querySelectorAll('a'))
                .filter(e => e.offsetParent !== null);
            links.push(document.querySelector('.js-nav-primary__mobile-toggle'));
            if (!this._element.contains(document.activeElement)) {
                links[0].focus();
            } else {
                let current = null;
                for (let i = 0; i < links.length; i++) {
                    if (links[i] === document.activeElement) {
                        current = i;
                        break;
                    }
                }
                if (current !== null) {
                    const i = (current + (e.shiftKey ? -1 : 1) + links.length) % links.length;
                    links[i].focus();
                }
            }
            e.preventDefault();
        }
    }

    _onBlur() {
        setTimeout(() => {
            if (!this._element.contains(document.activeElement) && this.isOpen
                && (document.activeElement === null || !document.activeElement.classList.contains('js-nav-primary__mobile-toggle'))) {
                this._openItem(this._element.querySelector('[aria-expanded=true]'), false);
            }
        }, 10);
    }

    _addEventListeners() {
        window.addEventListener('resize', this._onResize);
        window.addEventListener('keydown', this._onKeyPress);
        this._element.addEventListener('focusout', this._onBlur);
        this._megamenuItems.forEach(a => a.addEventListener('click', this._onItemClick));
    }

    _removeEventListeners() {
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('keydown', this._onKeyPress);
        this._element.removeEventListener('focusout', this._onBlur);
        this._megamenuItems.forEach(a => a.removeEventListener('click', this._onItemClick));
    }
}

export {
    PrimaryNavigation,
};
